import { FC, useEffect } from 'react';
import type { FieldAttributes, FieldProps } from 'formik';
import { DialogTitle, Description as DialogDescription } from '@headlessui/react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { useTracking } from '@/components/tracking-provider';
import Turnstile, { useTurnstile } from 'react-turnstile';
import { AuthResultSuccess, storeAuthSession } from '@/app/utils/authentication';
import {
  performLogin,
  loginValidationSchema,
  isAuthError,
  isAuthSuccess,
} from '@/app/utils/auth-api';

const InputFieldset = ({
  name,
  label,
  ...rest
}: FieldAttributes<{ label: string; autoComplete?: string }>) => (
  <div className="form-control">
    <label htmlFor={name}>
      <span>{label}</span>
      <ErrorMessage name={name} className="form-field-error" component="span" />
    </label>
    <Field id={name} name={name} {...rest} className="form-input" />
  </div>
);

interface LoginFormValues {
  base: string; // used for global error messages
  email: string;
  password: string;
  turnstile: string;
}

interface TurnstileInputComponentProps {
  siteKey: string;
  theme: 'auto' | 'light' | 'dark' | undefined;
}

const TurnstileField: FC<
  TurnstileInputComponentProps & FieldProps<{ value: string }, LoginFormValues>
> = ({
  siteKey,
  theme,
  form: { touched, errors, setFieldValue }, // values, dirty, isValid, status, etc.
}) => {
  return (
    <div className="h-[78px]">
      <Turnstile
        sitekey={siteKey}
        theme={theme}
        action="login"
        onVerify={(value) => setFieldValue('turnstile', value)}
      />
      {errors.turnstile && touched.turnstile ? (
        <div className="form-field-error">{errors.turnstile}</div>
      ) : null}
    </div>
  );
};

interface LoginFormProps {
  onSuccess?: (result: AuthResultSuccess) => void;
  onSwitchToSignup?: () => void;
  compact?: boolean;
  hideHeader?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function LoginForm({ onSuccess, onSwitchToSignup, compact, hideHeader }: LoginFormProps) {
  const tracker = useTracking();
  const turnstileTheme = 'light';
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const turnstile = useTurnstile();

  // onmount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => tracker.trackEvent('Login Begin', { state_name: 'index.next' }), []);

  return (
    <>
      {!hideHeader && (
        <>
          <DialogTitle className="mb-2">
            <span className="h3 font-merriweather">Log in</span>
          </DialogTitle>
          <DialogDescription className="mb-6">
            <span className="text-sm">
              Enter your email and password to log in to your Penzu account.
            </span>
            <br />
            <span className="text-sm">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="underline text-slate-800 hover:text-slate-600 font-medium cursor-pointer"
              >
                Sign up here.
              </button>
            </span>
          </DialogDescription>
        </>
      )}
      <Formik
        initialValues={{
          base: '',
          email: '',
          password: '',
          turnstile: '',
        }}
        validationSchema={loginValidationSchema}
        onSubmit={async (values, actions: FormikHelpers<LoginFormValues>) => {
          const result = await performLogin({
            email: values.email,
            password: values.password,
            turnstile: values.turnstile,
          });

          if (isAuthError(result)) {
            actions.setSubmitting(false);
            actions.setErrors({ base: `${result.error.title}: ${result.error.message}` });
            setTimeout(() => {
              // This clears the error message, so give it a short delay
              turnstile.reset();
            }, 1000);
          } else if (isAuthSuccess(result)) {
            try {
              storeAuthSession(result, tracker, 'Login Complete');
            } catch (error) {
              console.error('Unexpected error completing login:', error);
            }

            setTimeout(() => {
              if (onSuccess) {
                onSuccess(result);
              } else {
                window.location.href = '/journals';
              }
            }, 500);
          }
        }}
        onReset={() => turnstile.reset()}
      >
        {({ isSubmitting, isValid, dirty, errors }) => (
          <Form>
            {errors.base && <div className="form-error">{errors.base}</div>}
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4 mb-4">
              <InputFieldset
                name="email"
                label="Email"
                type="email"
                placeholder="eg. you@example.com"
                autoComplete="username"
              />
              <InputFieldset
                name="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </fieldset>
            <fieldset className="max-w-sm md:w-lg flex flex-col space-y-4 mb-6">
              <Field
                name="turnstile"
                component={TurnstileField}
                siteKey={turnstileSiteKey}
                theme={turnstileTheme}
              />
            </fieldset>
            <div className="mb-4">
              <button
                type="submit"
                className="btn text-white bg-blue-500 hover:bg-blue-700 w-full"
                disabled={isSubmitting || !isValid || !dirty}
              >
                Log in
              </button>
            </div>
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4">
              <div className="text-sm flex flex-row justify-between">
                <a
                  href="https://penzu.com/app/forgot-password"
                  className="underline text-slate-400 hover:text-slate-700 font-medium"
                >
                  Forgot password
                </a>

                <a
                  href="https://penzu.com/app/request-login-link"
                  className="underline text-slate-400 hover:text-slate-700 font-medium"
                >
                  Receive a login link via email instead
                </a>
              </div>
            </fieldset>
          </Form>
        )}
      </Formik>
    </>
  );
}
